import { useMemo, useState } from 'react'
import {
  NativeSyntheticEvent,
  Platform,
  TextInputChangeEventData,
  View,
} from 'react-native'
import { useTranslation } from 'react-i18next'
import Input from '@ui/forms/Input'
import Button from '@ui/buttons/Button'
import { useDiscounts, useTotalDiscountAmount } from '@order/useDiscounts'
import DiscountItem from '@order/DiscountItem'
import ConfirmationModal from '@ui/components/ConfirmationModal'
import { usePrice, usePriceFormatter } from 'hooks/useCurrency'
import { NumberUtils } from '@ca/common'
import Slider from '@ui/components/Slider'
import { useOrderResultTotal } from '@order/hooks/useOrderTotal'
import { useGetDiscountOrders } from '../useGetDiscountOrders'
import useAvailableCredits from '@order/hooks/useAvailableCredits'
import useSavedOrderState from '@order/hooks/useSavedOrderState'
import ModalWide from '@ui/components/Modal/variants/ModalWide'
import InputsSection from '@ui/forms/InputsSection'
import cn from '@ui/utils/cn'

const parse = NumberUtils.parse.toFloat

const normalizeAmount = (input: string, max: number) => {
  const parsed = NumberUtils.parse.toFloatOrNull(input)
  if (parsed == null || parsed <= 0) {
    return { valid: false as const }
  }

  const clamped = Math.min(parsed, max)
  return {
    valid: true as const,
    amount: clamped,
    normalized: clamped.toFixed(2),
  }
}

interface Props {
  open: boolean
  savedOrderId?: string
  close: () => void
}

export default function CreditsModal({ open, close, savedOrderId }: Props) {
  const { t } = useTranslation(['ui', 'order', 'user'])
  const text = 'order:discounts.credit.modal'
  const textForm = 'order:discounts.credit.modal.sections.form'

  const [value, setValue] = useState('')
  const [valid, setValid] = useState<boolean | null>(null)
  const [removeDiscountId, setRemoveDiscountId] = useState<string | null>(null)

  const discounts = useDiscounts(savedOrderId, {
    onSuccess: () => {
      setValue('')
    },
    onError: () => setValid(false),
  })

  const { result, deals, order } = useSavedOrderState(savedOrderId)

  const isPending = discounts.isPending
  const isEnabled = discounts.credit.enabled

  const formatter = usePriceFormatter()
  const credits = useAvailableCredits()
  const hasNoCredits = credits.available <= 0
  const isDisabled = isPending || !isEnabled || hasNoCredits

  const orderTotal = useOrderResultTotal({
    result,
    order,
    deals,
  })
  // We want the price without credit discounts

  const allowed = useMemo(() => {
    return {
      coupon: true,
      credit: false,
      referral: true,
      'gift-card': true,
      promotion: true,
    }
  }, [])

  const total = useTotalDiscountAmount({
    orderTotal,
    discounts: discounts.items,
    allowed,
  })

  const creditsMax = Math.min(credits.available, total.discounted)
  const minPrice = usePrice(0)
  const maxPrice = usePrice(creditsMax)

  const onChange = (event: NativeSyntheticEvent<TextInputChangeEventData>) => {
    if (!isEnabled || isDisabled) return
    setValue(event.nativeEvent.text)
    setValid(null)
  }

  const onBlur = () => {
    if (!isEnabled || isDisabled || !value) return
    const normalized = normalizeAmount(value, creditsMax)
    if (!normalized.valid) {
      setValid(false)
      return
    }
    setValue(normalized.normalized)
    setValid(null)
  }

  const getOrders = useGetDiscountOrders(order.items)

  const onSubmit = async () => {
    if (!isEnabled || isDisabled) return
    const normalized = normalizeAmount(value, creditsMax)
    if (!normalized.valid) {
      setValid(false)
      return
    }

    const amount = normalized.amount
    if (!result?.orders) return
    const mode = 'credit'
    const orders = getOrders({
      result,
      mode,
    })
    await discounts.add({
      mode,
      input: {
        orders,
        points: amount,
      },
    })
  }

  const onClose = () => {
    setValue('')
    setValid(null)
    close()
  }

  const list = discounts.credit.items
  const isUsed = list.length > 0

  const removeText = 'modals:discounts.remove'

  const handleConfirmRemove = async () => {
    if (!removeDiscountId) return
    await discounts.remove(removeDiscountId)
    setRemoveDiscountId(null)
  }

  return (
    <>
      <ModalWide
        open={open}
        close={onClose}
        title={t(`${text}.header.title`)}
        desc={t(`${text}.header.desc`)}
        hideBottomSpace
        center
        cardComponentProps={{
          justifyContent: Platform.OS === 'web' ? 'center' : 'flex-end',
        }}
        body={
          <View className="flex w-full flex-col gap-8 px-8 pb-8">
            {isUsed && (
              <InputsSection
                title={t(`${text}.sections.active.title`)}
                desc={t(`${text}.sections.active.desc`)}
              >
                <View className="flex flex-col overflow-hidden rounded-xl border border-black/10">
                  {list.map((item, index) => (
                    <DiscountItem
                      key={item.id}
                      title={`${t(`${text}.header.title`)} ${
                        list.length > 1 ? index + 1 : ''
                      }`}
                      index={index}
                      discount={item.discount}
                      onPress={() => setRemoveDiscountId(item.id)}
                    />
                  ))}
                </View>
              </InputsSection>
            )}
            {!isUsed && (
              <InputsSection
                inactive
                title={t(`${text}.sections.empty.title`)}
                desc={t(`${text}.sections.empty.desc`)}
              />
            )}
            <InputsSection
              inactive={isDisabled}
              title={t(`${textForm}.title`)}
              desc={t(`${textForm}.desc`)}
            >
              <View
                className={cn(
                  'flex flex-col items-stretch gap-8',
                  isDisabled && 'pointer-events-none'
                )}
              >
                <View className="flex flex-col gap-6">
                  <Input
                    inputMode="numeric"
                    label={t(`${textForm}.inputs.amount.label`)}
                    value={value}
                    disabled={isDisabled}
                    onChange={onChange}
                    onBlur={onBlur}
                    textEnd={t(`${textForm}.inputs.amount.textEnd`, {
                      total: formatter.format(credits.available),
                    })}
                    className="flex-1"
                    errorMessage={
                      valid === false ? t(`validation:error`) : undefined
                    }
                    successMessage={
                      valid === true ? t(`validation:success`) : undefined
                    }
                  />
                  <View
                    className={cn(
                      'h-12 justify-center overflow-hidden rounded-lg border border-black/10 bg-neutral-50',
                      isDisabled && 'border-black/0 bg-black/5'
                    )}
                  >
                    <Slider
                      disabled={isDisabled}
                      min={0}
                      max={creditsMax}
                      step={1}
                      value={parse(value || '0')}
                      buttonStart={minPrice}
                      buttonEnd={maxPrice}
                      showPercentageLabel={false}
                      hasError={valid === false}
                      onChange={(nextValue: number) => {
                        setValue(nextValue.toFixed(2))
                        setValid(null)
                      }}
                    />
                  </View>
                </View>
                <View className="flex flex-row">
                  <Button
                    variant="outlined"
                    disabled={isDisabled}
                    label={t(`${textForm}.cta`)}
                    className="w-max self-start"
                    onPress={onSubmit}
                    loading={isPending}
                  />
                </View>
              </View>
            </InputsSection>
          </View>
        }
      />
      <ConfirmationModal
        open={!!removeDiscountId}
        onClose={() => {
          setRemoveDiscountId(null)
        }}
        onCancel={() => {
          setRemoveDiscountId(null)
        }}
        onConfirm={handleConfirmRemove}
        isPending={isPending}
        title={t(`${removeText}.title`)}
        desc={t(`${removeText}.desc`)}
      />
    </>
  )
}
